import PageTitle from "../../../components/PageTitle";
import Button from "../../../components/Button";
import { useState, useEffect } from "react";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import { Icon } from "@iconify/react";
import Tabs from "@mui/lab/TabList";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TabContext from "@mui/lab/TabContext";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import { httprequest } from "../../../data/api";
import { useUserState } from "../../../context/UserContext";
import useWindowSize from "../../../hooks/useWindow";
import useValidator from "../../../hooks/useValidator";
import Modal from "../../../components/Modal";
import { Grid } from "@mui/material";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { accessViewLessonNoteDetails, accessCreateLessonNote } from "../../../data/roles";

const CLASS_ENDPOINT = "/api/v1/teacher/classes";
const LESSON_NOTE = "/api/v1/lesson-notes/teacher";
const TEACHER_CLASS_SUBJECT = "/api/v1/teacher/class-subject/";
const CREATE_LESSON_NOTE = "/api/v1/lesson-notes";

const AdminLessonNote = () => {
  const { userRole } = useUserState();
  const navigate = useNavigate();
  const { profile } = useUserState();
  const [value, setValue] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [classLevels, setClassLevels] = useState([]);
  const [allLessonNotes, setAllLessonNotes] = useState([]);
  const [isClassLoading, setIsClassLoading] = useState(false);
  const [isSubjectLoading, setIsSubjectLoading] = useState(false);
  const [createNoteModal, setCreateNoteModal] = useState(false);
  const [classValidator, showClassValidator] = useValidator();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [lessonNoteToDelete, setLessonNoteToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [lessonNoteId, setLessonNoteId] = useState("");
  const [lessonNoteForm, setLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });
  const [editLessonNoteForm, setEditLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });

  /**
 * Handles the change of table tabs.
 * @param {React.SyntheticEvent} event - The tab change event.
 * @param {string} newValue - The new tab value.
 */
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  /**
 * Handles changes to select input fields.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setLessonNoteForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
 * Handles changes to select input fields in the edit form.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleEditSelectChange = (e) => {
    const { name, value } = e.target;
    setLessonNoteForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
 * Fetches all subjects for a given class ID.
 * @param {string} class_id - The ID of the class to fetch subjects for.
 */
  const getAllSubjects = (class_id) => {
    setIsSubjectLoading(true);
    httprequest
      .get(`${TEACHER_CLASS_SUBJECT}${class_id}`)
      .then((res) => {
        setAllSubjects(res?.data?.data);
        setIsSubjectLoading(false);
      })
      .catch((err) => {
        setIsSubjectLoading(false);
        toast.error(err?.response?.data);
      });
  };

  /**
 * Handles class selection change and triggers fetching subjects for that class.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleclassChange = (e) => {
    const { name, value } = e.target;
    setLessonNoteForm((prev) => ({ ...prev, [name]: value }));
    getAllSubjects(value);
  };

  /**
 * Handles class selection change in the edit form and triggers fetching subjects for that class.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleEditClassChange = (e) => {
    const { name, value } = e.target;
    setEditLessonNoteForm((prev) => ({ ...prev, [name]: value }));
    getAllSubjects(value);
  };

  const width = useWindowSize();
  const mobile = width < 600;

  /**
 * Toggles the create note modal open/close state.
 */
  const handleCreateNoteModal = () => {
    setCreateNoteModal(!createNoteModal);
  };

  /**
 * Closes the create lesson note modal and resets the lesson note form.
 */
  const handleCloseLessonNoteModal = () => {
    setCreateNoteModal(false);
    setLessonNoteForm({ subject_id: "", class_id: "" });
  };

  /**
 * Fetches all class levels.
 */
  const getClassLevels = () => {
    setIsClassLoading(false);
    httprequest
      .get(CLASS_ENDPOINT)
      .then((res) => {
        setClassLevels(res?.data?.data);
        setIsClassLoading(false);
      })
      .catch((err) => {
        setIsClassLoading(false);
        toast.error(err?.response?.data);
      });
  };

  /**
 * Fetches all lesson notes for the selected class.
 * When on 'All' tab (value=""), fetches notes for all teacher-assigned classes and merges them.
 */
  const getAllLessonNotes = async () => {
    try {
      if (value === "") {
        // On 'All' tab - fetch and merge notes from all teacher's classes to avoid seeing other classes' notes
        const classes = classLevels?.length ? classLevels : await httprequest.get(CLASS_ENDPOINT).then(r => r?.data?.data || []);
        if (!classes || classes.length === 0) {
          setAllLessonNotes([]);
          setSearchResult([]);

          return;
        }
        const results = await Promise.all(
          classes.map((cls) => httprequest.get(`${LESSON_NOTE}?class_id=${cls?.class?.id}`)
            .then((res) => res?.data?.data || [])
            .catch(() => [])
          )
        );
        const merged = results.flat();
        setAllLessonNotes(merged);
        setSearchResult(merged);
      } else {
        const res = await httprequest.get(`${LESSON_NOTE}?class_id=${value}`);
        setAllLessonNotes(res?.data?.data || []);
        setSearchResult(res?.data?.data || []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch lesson notes");
    }
  };

  /**
 * Creates a new lesson note after validating the form.
 */
  const handleCreateLessonNote = () => {
    if (classValidator.allValid()) {
      setIsLoading(true);
      httprequest
        .post(CREATE_LESSON_NOTE, lessonNoteForm)
        .then((res) => {
          toast.success(res?.data?.message);
          getAllLessonNotes();
          handleCreateNoteModal();
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          toast.error(err?.response?.data?.message);
        });
    } else {
      setIsLoading(false);
      showClassValidator(true);
    }
  };

  // Edit modal trigger
  const handleEditModal = (row) => {
    setEditLessonNoteForm({ ...lessonNoteForm, subject_id: row?.subject_id, class_id: row?.class_id });
    setLessonNoteId(row?.id);
    setEditModal(!editModal);
  };

  /**
 * Closes the edit modal and resets the edit lesson note form.
 */
  const closeEditModal = () => {
    setEditLessonNoteForm({ ...lessonNoteForm, subject_id: "", class_id: "" });
    setLessonNoteId("");
    setEditModal(false);
  };

  /**
 * Sends a patch request to update an existing lesson note.
 * @param {string} lessonNoteId - The ID of the lesson note to update.
 */
  const handlePatchLessonNote = (lessonNoteId) => {
    httprequest
      .patch(`${CREATE_LESSON_NOTE}/${lessonNoteId}`, editLessonNoteForm)
      .then((res) => {
        toast.success(res?.data?.message);
        getAllLessonNotes();
        closeEditModal();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * Toggles the delete confirmation modal for a lesson note.
 * @param {string} lessonNoteId - The ID of the lesson note to delete.
 */
  const handleDeleteModal = (lessonNoteId) => {
    setLessonNoteToDelete(lessonNoteId);
    setDeleteModal(!deleteModal);
  };

  /**
 * Deletes a lesson note by ID and updates the list of lesson notes.
 * @param {string} lessonNoteId - The ID of the lesson note to delete.
 */
  const deleteLessonNote = (lessonNoteId) => {
    httprequest.delete(`${CREATE_LESSON_NOTE}/${lessonNoteId}`)
      .then(() => {
        toast.success("Lesson note deleted successfully");
        setAllLessonNotes((prevNotes) => prevNotes.filter((lessonnote) => lessonnote.id !== lessonNoteId));
        setDeleteModal(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * Navigates to the lesson note view page for a specific lesson note.
 * @param {Object} row - The lesson note data object.
 */
  const navigateViewLesson = (row) => {
    navigate(`/lesson-notes/${row?.id}`);
  };

  const columns = [
    {
      name: "Class",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Class</p>}
          {row?.class?.name || "--/--"}
        </>
      )
    },
    {
      name: "Subject",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Subject</p>}
          {row?.subject?.name || "--/--"}
        </>
      )
    },
    {
      name: "Note Count",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Note Count</p>}
          {row?.outline_count || "--/--"}
        </>
      )
    },
    {
      name: "Created at",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Created at</p>}
          {row?.created_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "Last Updated",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Last Updated</p>}
          {row?.updated_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "",
      center: true,
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          {accessViewLessonNoteDetails(userRole) &&
          <>
            <button
              className="table-action-icon"
              onClick={() => navigateViewLesson(row)}
            >
              <Icon icon="fluent:eye-16-regular" />
            </button>
            <button
              className="table-action-icon"
              onClick={() => {
                handleEditModal(row);
                getAllSubjects(row?.class?.id);
              }}
            >
              <Icon icon="heroicons:pencil-square-solid" />
            </button>
            <button
              className="table-action-icon delete-icon"
              onClick={() => handleDeleteModal(row?.id)}
            >
              <Icon icon="mingcute:delete-2-line" />
            </button>
          </>
          }
        </div>
      )
    }
  ];

  useEffect(() => {
    getClassLevels();
  }, []);

  useEffect(() => {
    getAllLessonNotes();
  }, [value]);

  /**
 * Filters lesson notes based on search input matching class or subject names.
 * @param {event parameter passed on handleChange} e - The input change event.
 */
  const handleLessonNoteSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = allLessonNotes?.filter((data) => valArray?.every(
      (word) => data?.class?.name?.toLowerCase().includes(word.toLowerCase()) ||
      data?.subject?.name?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  const { subject_id, class_id } = lessonNoteForm;

  return (
    <div>
      <PageTitle
        title="Lesson Note"
        subtitle="See all your Lesson Note"
        button={
          accessCreateLessonNote(userRole) && (
            <div>
              <Button
                variant="primary"
                buttonSize="fluid"
                color="btndefault"
                onClick={() => handleCreateNoteModal()}
                buttonName="Create Lesson Note"
              />
            </div>
          )
        }
      />
      <div className="page-content">
        <TabContext value={value}>
          <div className="tablist-container padding-left-16">
            <Tabs
              className="overide-tablist"
              onChange={handleTabChange}
              aria-label="lab API tabs example"
            >
              <Tab label="All Lesson note" value="" data={profile} />
              {isClassLoading ? (
                <div className="container-left">
                  <Icon icon="eos-icons:three-dots-loading" width="50" height="50" />
                </div>
              ) : (
                classLevels?.map((data) => {
                  return (
                    <Tab
                      key={data?.class?.id}
                      value={data?.class?.id}
                      label={data?.class?.name}
                    />
                  );
                })
              )}
            </Tabs>
          </div>
          <>
            <TabPanel value="" className="tabpanel-overide">
              <Table
                data={searchResult}
                columns={columns}
                subHeader={true}
                pagination
                subHeaderComponent={
                  <TableSearch
                    placeholder="Search here..."
                    searchTableFunc={handleLessonNoteSearch}
                  />
                }
              />
            </TabPanel>
            {classLevels?.map((data) => {
              return (
                <TabPanel sx={{ padding: "0px !important" }} key={data?.class?.id} value={data?.class?.id}>
                  <Table
                    data={allLessonNotes}
                    columns={columns}
                    subHeader={true}
                    pagination
                    subHeaderComponent={<TableSearch placeholder="Search here..." searchTableFunc={handleLessonNoteSearch} />}
                  />
                </TabPanel>
              );
            })}
          </>
        </TabContext>
        {/* Create Lesson NOte */}
        <Modal
          title="Create Lesson Note"
          modalContent={
            <>
              <Grid container spacing={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <SelectGroup
                    label="Class"
                    required
                    children={
                      <select onChange={handleclassChange} name="class_id">
                        <option value="">--- Select ---</option>
                        {classLevels?.map((classes) => {
                          return (
                            <option key={classes?.class?.id} value={classes?.class?.id}>
                              {classes?.class?.name}
                            </option>
                          );
                        })}
                      </select>
                    }
                  />
                  {classValidator.message("class_name", class_id, "required")}
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <SelectGroup
                    label="Subject"
                    required
                    children={
                      <select onChange={handleSelectChange} name="subject_id">
                        <option value="">--- Select ---</option>
                        {allSubjects?.map((subject) => {
                          return (
                            <option key={subject?.subject?.id} value={subject?.subject?.id}>
                              {subject?.subject?.name}
                            </option>
                          );
                        })}
                      </select>
                    }
                  />
                  {classValidator.message("subject_id", subject_id, "required")}
                </Grid>
              </Grid>
            </>
          }
          modalFooter={
            <>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Create Lesson Note"
                onClick={handleCreateLessonNote}
                isLoading={isLoading}
              />
            </>
          }
          isopen={createNoteModal}
          closeModal={handleCloseLessonNoteModal}
        />
        {/* Edit Modal */}
        <Modal
          title="Edit Lesson Note"
          modalContent={
            <>
              <Grid container spacing={2}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <SelectGroup
                    label="Class"
                    required
                    children={
                      <select onChange={handleEditClassChange} name="class_id" defaultValue={editLessonNoteForm?.class_id}>
                        <option value="">--- Select ---</option>
                        {classLevels?.map((classes) => {
                          return (
                            <option key={classes?.class?.id} value={classes?.class?.id}>
                              {classes?.class?.name}
                            </option>
                          );
                        })}
                      </select>
                    }
                  />
                  {classValidator.message("class_name", class_id, "required")}
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  {isSubjectLoading ? (
                    <div className="container-left">
                      <Icon icon="eos-icons:three-dots-loading" width="50" height="50" />
                    </div>
                  ) : (
                    <SelectGroup
                      label="Subject"
                      required
                      children={
                        <select onChange={handleEditSelectChange} name="subject_id" defaultValue={editLessonNoteForm?.subject_id}>
                          <option value="">--- Select ---</option>
                          {allSubjects?.map((subject) => {
                            return (
                              <option key={subject?.subject?.id} value={subject?.subject?.id}>
                                {subject?.subject?.name}
                              </option>
                            );
                          })}
                        </select>
                      }
                    />
                  )}
                  {classValidator.message("subject_id", subject_id, "required")}
                </Grid>
              </Grid>
            </>
          }
          modalFooter={
            <>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Edit Lesson Note"
                onClick={() => handlePatchLessonNote(lessonNoteId)}
                isLoading={isLoading}
              />
            </>
          }
          isopen={editModal}
          closeModal={closeEditModal}
        />
        {/* Delete Modal */}
        <Modal
          title="Delete Lesson Note"
          subtitle="Are you sure you want to permanently delete this Lesson Note?"
          modalContent={<></>}
          modalFooter={
            <>
              <div className="flex gap-10">
                <Button
                  variant="dangeroutline"
                  buttonSize="full"
                  color="btndanger"
                  buttonName="Cancel"
                  onClick={handleDeleteModal}
                />
                <Button
                  variant="danger"
                  buttonName="Delete Lesson note"
                  buttonSize="full"
                  color="btndefault"
                  onClick={() => deleteLessonNote(lessonNoteToDelete)}
                />
              </div>
            </>
          }
          isopen={deleteModal}
          closeModal={handleDeleteModal}
        />
        {/* <ToastContainer /> */}
      </div>
    </div>
  );
};

export default AdminLessonNote;
